/**
 * CloudMap UI
 * UI setup and interaction handlers
 */

import {SUPER_REGIONS, getProviderColor} from './constants.js';

/**
 * Setup provider toggle buttons (card style)
 */
export function setupProviderCards(container, mapId, data, state, callbacks) {
    const providersContainer = container.querySelector(`#${mapId}-providers`);
    if (!providersContainer || !data?.providers) return;

    providersContainer.innerHTML = '';

    // Get default providers from data attribute
    const defaultProviders = container.dataset.defaultProviders;
    if (defaultProviders && state.activeProviders.size === 0) {
        defaultProviders.split(',').forEach(p => {
            state.activeProviders.add(p.trim());
        });
    }

    // If no providers selected, select all
    if (state.activeProviders.size === 0) {
        data.providers.forEach(p => {
            state.activeProviders.add(p.slug);
        });
    }

    data.providers.forEach(provider => {
        const isActive = state.activeProviders.has(provider.slug);
        const color = getProviderColor(provider.slug);
        const card = document.createElement('button');
        card.type = 'button';
        card.className = `cloudmap-provider-card ${isActive ? 'active' : ''}`;
        card.style.setProperty('--provider-color', color);
        card.setAttribute('aria-pressed', isActive);
        card.setAttribute('data-provider', provider.slug);

        card.innerHTML = `
            <div class="cloudmap-provider-card__header">
                <span class="cloudmap-provider-card__dot" style="background-color: ${color}"></span>
                <span class="cloudmap-provider-card__name">${provider.name}</span>
            </div>
            <div class="cloudmap-provider-card__stats">
                <div class="cloudmap-provider-card__stat">
                    <span class="cloudmap-provider-card__stat-value">${provider.regionCount}</span>
                    <span class="cloudmap-provider-card__stat-label">Regions</span>
                </div>
                <div class="cloudmap-provider-card__stat">
                    <span class="cloudmap-provider-card__stat-value">${provider.activeCount}</span>
                    <span class="cloudmap-provider-card__stat-label">Active</span>
                </div>
                <div class="cloudmap-provider-card__stat">
                    <span class="cloudmap-provider-card__stat-value">${provider.plannedCount}</span>
                    <span class="cloudmap-provider-card__stat-label">Planned</span>
                </div>
            </div>
        `;

        card.addEventListener('click', () => callbacks.onToggle(provider.slug));

        providersContainer.appendChild(card);
    });

    // Setup clear filters
    const clearLink = container.querySelector(`#${mapId}-clear`);
    if (clearLink) {
        clearLink.addEventListener('click', (e) => {
            e.preventDefault();
            callbacks.onClear();
        });
    }
}

/**
 * Update provider card states
 */
export function updateCardStates(container, activeProviders) {
    const cards = container.querySelectorAll('.cloudmap-provider-card');
    cards.forEach(card => {
        const providerSlug = card.dataset.provider;
        const isActive = activeProviders.has(providerSlug);
        card.classList.toggle('active', isActive);
        card.setAttribute('aria-pressed', isActive);
    });
}

/**
 * Update clear filters visibility
 */
export function updateClearFiltersVisibility(container, mapId, activeProviders, totalProviders) {
    const clearLink = container.querySelector(`#${mapId}-clear`);
    if (!clearLink) return;

    const allSelected = activeProviders.size === totalProviders;
    clearLink.style.display = allSelected ? 'none' : 'inline';
}

/**
 * Setup super region quick zoom pills
 */
export function setupSuperRegions(container, mapId, onZoom) {
    const regionsContainer = container.querySelector(`#${mapId}-super-regions`);
    if (!regionsContainer) return;

    regionsContainer.innerHTML = '';

    SUPER_REGIONS.forEach((region, index) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'cloudmap-super-region-pill';
        if (index === 0) pill.classList.add('active');
        pill.textContent = region.name;
        pill.setAttribute('data-region', region.name);

        pill.addEventListener('click', () => {
            const pills = container.querySelectorAll('.cloudmap-super-region-pill');
            pills.forEach(p => p.classList.remove('active'));
            pill.classList.add('active');
            onZoom(region);
        });

        regionsContainer.appendChild(pill);
    });
}

/**
 * Setup legend
 */
export function setupLegend(container, mapId, providers) {
    const legend = container.querySelector(`#${mapId}-legend`);
    if (!legend || !providers) return;

    legend.innerHTML = '';

    providers.forEach(provider => {
        const color = getProviderColor(provider.slug);
        const item = document.createElement('div');
        item.className = 'cloudmap-legend__item';
        item.innerHTML = `
            <span class="cloudmap-legend__dot" style="background-color: ${color}"></span>
            <span class="cloudmap-legend__label">${provider.name}</span>
        `;
        legend.appendChild(item);
    });
}

/**
 * Update region count display
 */
export function updateCount(container, count, i18n) {
    const countEl = container.querySelector('.cloudmap-count__number');
    if (countEl) {
        countEl.textContent = count;
    }

    const labelEl = container.querySelector('.cloudmap-count__label');
    if (labelEl) {
        labelEl.textContent = count === 1 ? i18n.region : i18n.regions;
    }
}

/**
 * Announce message to screen readers
 */
export function announce(container, mapId, message) {
    const announcer = container.querySelector(`#${mapId}-announcements`);
    if (announcer) {
        announcer.textContent = message;
    }
}

/**
 * Show loading state
 */
export function showLoading(container) {
    const loading = container.querySelector('.cloudmap-loading');
    if (loading) {
        loading.classList.remove('hidden');
    }
}

/**
 * Hide loading state
 */
export function hideLoading(container) {
    const loading = container.querySelector('.cloudmap-loading');
    if (loading) {
        loading.classList.add('hidden');
    }
}

/**
 * Show error message
 */
export function showError(container, message) {
    const loading = container.querySelector('.cloudmap-loading');
    if (loading) {
        loading.innerHTML = `
            <div class="cloudmap-error">
                <p>${message}</p>
                <button onclick="location.reload()">Retry</button>
            </div>
        `;
    }
}
